import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

export interface LocationInfo {
  country: string;
  city: string;
  state?: string;
  formattedAddress: string;
  zoneId?: string;
  zoneType?: 'hardiness' | 'climate';
}

export class LocationService {
  /**
   * Check if we're running on a native platform or web
   */
  private static isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get current GPS coordinates - works on both web and mobile
   */
  static async getCurrentPosition(): Promise<LocationCoordinates> {
    try {
      if (this.isNativePlatform()) {
        // Use Capacitor Geolocation for mobile apps
        const permissions = await Geolocation.requestPermissions();
        
        if (permissions.location !== 'granted') {
          throw new Error('Location permission denied');
        }

        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        });

        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
      } else {
        // Use Web Geolocation API for browser
        return new Promise((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by this browser'));
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            },
            (error) => {
              let errorMessage = 'Failed to get location';
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage = 'Location access denied by user';
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage = 'Location information unavailable';
                  break;
                case error.TIMEOUT:
                  errorMessage = 'Location request timed out';
                  break;
              }
              reject(new Error(errorMessage));
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 60000
            }
          );
        });
      }
    } catch (error) {
      console.error('Error getting GPS location:', error);
      throw error;
    }
  }

  /**
   * Determine USDA hardiness zone based on coordinates (for US)
   * Updated with more accurate regional mappings
   */
  private static getUSDAZone(lat: number, lon: number): string {
    // More accurate zone mapping considering regional climate patterns
    
    // Alaska zones
    if (lat >= 64) return "1a";
    if (lat >= 60) return "1b";
    if (lat >= 56) return "2a";
    if (lat >= 51) return "2b";
    
    // Northern states
    if (lat >= 47) return "3a";
    if (lat >= 45) return "3b";
    if (lat >= 42) return "4a";
    if (lat >= 40) return "4b";
    if (lat >= 38) return "5a";
    if (lat >= 36) return "5b";
    
    // Mid-Atlantic and transition zones
    if (lat >= 35) return "6a";
    
    // Southern states with regional adjustments
    if (lat >= 33.5) {
      // Georgia, South Carolina, North Carolina - generally warmer zones
      if (lon >= -85 && lon <= -80) {
        // Georgia/South Carolina region
        if (lat >= 34) return "7a";
        return "8b"; // Southern Georgia like Evans area
      }
      return "6b";
    }
    
    // Deep South and warmer regions  
    if (lat >= 32) return "7a";
    if (lat >= 30) return "7b";
    if (lat >= 28) return "8a";
    if (lat >= 26) return "8b";
    if (lat >= 24) return "9a";
    if (lat >= 20) return "9b";
    
    return "10a"; // Tropical
  }

  /**
   * Determine RHS hardiness zone based on coordinates (for UK)
   */
  private static getRHSZone(lat: number, lon: number): string {
    // Simplified RHS (Royal Horticultural Society) zone mapping for UK
    if (lat >= 58) return "H7"; // Scottish Highlands - coldest
    if (lat >= 56) return "H6"; // Northern Scotland
    if (lat >= 54) return "H5"; // Southern Scotland, Northern England
    if (lat >= 52) return "H4"; // Central England
    if (lat >= 50) return "H3"; // Southern England
    return "H2"; // Southwest England, coastal areas - mildest
  }

  /**
   * Determine Canadian hardiness zone based on coordinates
   */
  private static getCanadianZone(lat: number, lon: number): string {
    // Simplified Canadian zone mapping
    if (lat >= 60) return "0a"; // Arctic
    if (lat >= 55) return "1a"; // Far north
    if (lat >= 50) return "2a"; // Northern regions
    if (lat >= 45) return "3a"; // Southern Canada
    return "4a"; // Southern border
  }

  /**
   * Determine Australian hardiness zone based on coordinates
   */
  private static getAustralianZone(lat: number, lon: number): string {
    // Simplified Australian zone mapping (latitude is negative in southern hemisphere)
    const absLat = Math.abs(lat);
    if (absLat >= 40) return "AUS1"; // Tasmania, southern regions
    if (absLat >= 35) return "AUS2"; // Southern Australia
    if (absLat >= 30) return "AUS3"; // Central Australia
    if (absLat >= 25) return "AUS4"; // Northern regions
    return "AUS5"; // Tropical north
  }

  /**
   * Determine appropriate climate classification for other countries
   */
  private static getClimateClassification(country: string, lat: number, lon: number): string {
    const absLat = Math.abs(lat);
    
    // Basic climate classification based on latitude
    if (absLat >= 60) return "Arctic";
    if (absLat >= 50) return "Subarctic";
    if (absLat >= 40) return "Continental";
    if (absLat >= 30) return "Temperate";
    if (absLat >= 23.5) return "Subtropical";
    return "Tropical";
  }

  /**
   * Get appropriate zone (hardiness or climate) based on location
   */
  private static async determineZone(country: string, lat: number, lon: number): Promise<{ zoneId: string | null; zoneType: 'hardiness' | 'climate' }> {
    if (country === 'United States') {
      try {
        // First try precise polygon-based lookup via PostGIS
        const { data: zone_id, error: rpcError } = await supabase.rpc('get_usda_zone_id_by_coordinates', {
          lat,
          lng: lon,
        });

        if (!rpcError && zone_id) {
          return { zoneId: zone_id as string, zoneType: 'hardiness' };
        }

        // Fallback to heuristic mapping if RPC returns nothing
        const usZone = this.getUSDAZone(lat, lon);
        const { data: zones } = await supabase
          .from('hardiness_zones')
          .select('id')
          .eq('zone_code', usZone)
          .eq('zone_system', 'USDA')
          .limit(1);

        return {
          zoneId: zones && zones.length > 0 ? zones[0].id : null,
          zoneType: 'hardiness',
        };
      } catch (error) {
        // On any error, still fall back to heuristic
        const usZone = this.getUSDAZone(lat, lon);
        const { data: zones } = await supabase
          .from('hardiness_zones')
          .select('id')
          .eq('zone_code', usZone)
          .eq('zone_system', 'USDA')
          .limit(1);
        return { zoneId: zones && zones.length > 0 ? zones[0].id : null, zoneType: 'hardiness' };
      }
    } else if (country === 'United Kingdom') {
      const ukZone = this.getRHSZone(lat, lon);
      // Find the zone ID in the database
      try {
        const { data: zones } = await supabase
          .from('hardiness_zones')
          .select('id')
          .eq('zone_code', ukZone)
          .eq('zone_system', 'RHS')
          .limit(1);
        
        return {
          zoneId: zones && zones.length > 0 ? zones[0].id : null,
          zoneType: 'hardiness'
        };
      } catch (error) {
        return { zoneId: null, zoneType: 'hardiness' };
      }
    } else if (country === 'Canada') {
      const canZone = this.getCanadianZone(lat, lon);
      try {
        const { data: zones } = await supabase
          .from('hardiness_zones')
          .select('id')
          .eq('zone_code', canZone)
          .eq('zone_system', 'Canadian')
          .limit(1);
        
        return {
          zoneId: zones && zones.length > 0 ? zones[0].id : null,
          zoneType: 'hardiness'
        };
      } catch (error) {
        return { zoneId: null, zoneType: 'hardiness' };
      }
    } else if (country === 'Australia') {
      const ausZone = this.getAustralianZone(lat, lon);
      try {
        const { data: zones } = await supabase
          .from('hardiness_zones')
          .select('id')
          .eq('zone_code', ausZone)
          .eq('zone_system', 'Australian')
          .limit(1);
        
        return {
          zoneId: zones && zones.length > 0 ? zones[0].id : null,
          zoneType: 'hardiness'
        };
      } catch (error) {
        return { zoneId: null, zoneType: 'hardiness' };
      }
    } else {
      // For other countries, use climate classification as the zone info
      const climateZone = this.getClimateClassification(country, lat, lon);
      return {
        zoneId: climateZone, // Use the climate classification as the zone identifier
        zoneType: 'climate'
      };
    }
  }
  /**
   * Reverse geocode coordinates to get location information
   * Using a free geocoding service (OpenStreetMap Nominatim)
   */
  static async reverseGeocode(coordinates: LocationCoordinates): Promise<LocationInfo> {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coordinates.latitude}&lon=${coordinates.longitude}&zoom=10&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Geocoding failed');
      }

      const data = await response.json();
      
      const address = data.address || {};
      const country = address.country || '';
      const city = address.city || address.town || address.village || address.municipality || '';
      const state = address.state || address.province || '';
      
      // Determine appropriate zone based on location
      const { zoneId, zoneType } = await this.determineZone(country, coordinates.latitude, coordinates.longitude);
      
      return {
        country,
        city,
        state,
        formattedAddress: data.display_name || '',
        zoneId: zoneId || undefined,
        zoneType
      };
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      throw error;
    }
  }

  /**
   * Get location using GPS and return formatted location info
   */
  static async getGPSLocation(): Promise<LocationInfo> {
    const coordinates = await this.getCurrentPosition();
    return await this.reverseGeocode(coordinates);
  }
}

// Common countries list for manual selection
export const countries = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Denmark',
  'Sweden',
  'Norway',
  'Finland',
  'Ireland',
  'Portugal',
  'Greece',
  'Poland',
  'Czech Republic',
  'Hungary',
  'Slovakia',
  'Slovenia',
  'Croatia',
  'Bulgaria',
  'Romania',
  'Lithuania',
  'Latvia',
  'Estonia',
  'Luxembourg',
  'Malta',
  'Cyprus',
  'Japan',
  'South Korea',
  'China',
  'India',
  'Singapore',
  'Malaysia',
  'Thailand',
  'Philippines',
  'Indonesia',
  'Vietnam',
  'Taiwan',
  'Hong Kong',
  'New Zealand',
  'South Africa',
  'Brazil',
  'Argentina',
  'Chile',
  'Mexico',
  'Colombia',
  'Peru',
  'Venezuela',
  'Uruguay',
  'Paraguay',
  'Bolivia',
  'Ecuador',
  'Costa Rica',
  'Panama',
  'Guatemala',
  'Honduras',
  'Nicaragua',
  'El Salvador',
  'Belize',
  'Jamaica',
  'Trinidad and Tobago',
  'Barbados',
  'Bahamas'
].sort();

// US Cities list (for when US is selected)
export const usCities = [
  'New York, NY',
  'Los Angeles, CA',
  'Chicago, IL',
  'Houston, TX',
  'Phoenix, AZ',
  'Philadelphia, PA',
  'San Antonio, TX',
  'San Diego, CA',
  'Dallas, TX',
  'San Jose, CA',
  'Austin, TX',
  'Jacksonville, FL',
  'Fort Worth, TX',
  'Columbus, OH',
  'Indianapolis, IN',
  'Charlotte, NC',
  'San Francisco, CA',
  'Seattle, WA',
  'Denver, CO',
  'Oklahoma City, OK',
  'Nashville, TN',
  'El Paso, TX',
  'Washington, DC',
  'Boston, MA',
  'Las Vegas, NV',
  'Portland, OR',
  'Detroit, MI',
  'Louisville, KY',
  'Memphis, TN',
  'Baltimore, MD',
  'Milwaukee, WI',
  'Albuquerque, NM',
  'Fresno, CA',
  'Tucson, AZ',
  'Sacramento, CA',
  'Mesa, AZ',
  'Kansas City, MO',
  'Atlanta, GA',
  'Omaha, NE',
  'Colorado Springs, CO',
  'Raleigh, NC',
  'Miami, FL',
  'Long Beach, CA',
  'Virginia Beach, VA',
  'Oakland, CA',
  'Minneapolis, MN',
  'Tampa, FL',
  'Tulsa, OK',
  'Arlington, TX',
  'New Orleans, LA'
].sort();